import React, { useEffect } from "react";
import { motion } from "framer-motion";
import CTA from "./Components/CTA";

const UiDesign = () => {
    useEffect(() => {
        document.title = "Agentic UI/UX & MCP Tool Integration | Jibu Labs";
        
        const metaDescription = document.querySelector('meta[name="description"]');
        if (metaDescription) {
            metaDescription.setAttribute("content", "Jibu Labs designs next-generation Agentic UI/UX interfaces and Model Context Protocol (MCP) tool integrations for seamless human-AI workflows.");
        }
    }, []);

    const pillars = [
        {
            title: "Human-in-the-Loop Layouts",
            description: "We design review-and-confirm dashboards that enable human operators to verify and edit agentic suggestions, keeping business critical paths safe.",
            icon: (
                <svg className="w-8 h-8 text-forest" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
            )
        },
        {
            title: "Predictive & Low-Friction Inputs",
            description: "We optimize input fields with autocomplete tools, proactive chips, and real-time generation preview states to accelerate human-agent interaction velocity.",
            icon: (
                <svg className="w-8 h-8 text-forest" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                </svg>
            )
        },
        {
            title: "Explainability & Citations",
            description: "We build interfaces that detail reasoning paths, display confidence percentages, and present source documents inline so users can trust the model's conclusions.",
            icon: (
                <svg className="w-8 h-8 text-forest" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
            )
        },
        {
            title: "Model Context Protocol (MCP)",
            description: "We design context-aware tool-calling panels for Model Context Protocol (MCP) integrations, connecting autonomous agents to private data structures.",
            icon: (
                <svg className="w-8 h-8 text-forest" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
            )
        }
    ];

    return (
        <div className="select-none min-h-screen bg-[#fafafa] dark:bg-[#191a1a] transition-colors duration-300">
            {/* Hero Section */}
            <section className="max-w-4xl mx-auto px-6 py-16 lg:py-24 text-center space-y-6">
                <motion.div 
                    initial={{ opacity: 0, y: -10 }} 
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                >
                    <span className="text-xs font-semibold text-forest uppercase tracking-widest bg-forest/10 px-3 py-1.5 rounded-full">AI UX/UI</span>
                </motion.div>
                <motion.h1 
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.1 }}
                    className="text-4xl lg:text-5xl font-extrabold text-zinc-900 dark:text-white tracking-tight leading-tight"
                >
                    Agentic UI/UX Design
                </motion.h1>
                <motion.p 
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.5, delay: 0.25 }}
                    className="text-base lg:text-lg text-zinc-500 dark:text-zinc-400 font-light max-w-2xl mx-auto leading-relaxed"
                >
                    We design clean, intuitive, explainable user interfaces that optimize cooperation between corporate operators and backend autonomous AI pipelines.
                </motion.p>
            </section>

            {/* Content Cards Section */}
            <section className="max-w-6xl mx-auto px-6 pb-16">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    {pillars.map((p, idx) => (
                        <motion.div
                            key={idx}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: 0.2 + idx * 0.1 }}
                            className="bg-white dark:bg-zinc-900/60 p-8 rounded-3xl border border-zinc-200 dark:border-zinc-800 hover:border-forest/20 shadow-[0_4px_20px_rgba(0,0,0,0.01)] transition-all duration-300"
                        >
                            <div className="w-12 h-12 rounded-2xl bg-emerald-500/10 flex items-center justify-center mb-6">
                                {p.icon}
                            </div>
                            <h3 className="text-xl font-bold text-zinc-900 dark:text-white mb-3">{p.title}</h3>
                            <p className="text-sm font-light text-zinc-500 dark:text-zinc-400 leading-relaxed">
                                {p.description}
                            </p>
                        </motion.div>
                    ))}
                </div>
            </section>

            {/* CTA section */}
            <CTA />
        </div>
    );
};

export default UiDesign;